/**
 * 可收起展开的附件区域组件
 * - 基于 email-content-display/AttachmentArea.tsx 的逻辑
 * - 支持展开/收起功能
 * - 自动检测是否需要显示展开按钮
 * - 使用现有的 AttachmentCard 组件
 */

import { forwardRef, useImperativeHandle, useState, useRef, useEffect } from 'react';
import { Paperclip, ChevronDown, ChevronUp } from 'lucide-react';
import { AttachmentsModel } from '@/stores/mailEditorStore';
import AttachmentCard from './AttachmentCard';

interface CollapsibleAttachmentAreaProps {
  attachments: AttachmentsModel[];
  onDeleteAttachment: (identity: string) => void;
  onReuploadAttachment: (identity: string) => void;
}

export interface CollapsibleAttachmentAreaRef {
  scrollToAttachment: () => void;
}

const CollapsibleAttachmentArea = forwardRef<CollapsibleAttachmentAreaRef, CollapsibleAttachmentAreaProps>(
  ({ attachments, onDeleteAttachment, onReuploadAttachment }, ref) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showExpandButton, setShowExpandButton] = useState(false);
    const listRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);

    // 暴露方法给父组件
    useImperativeHandle(ref, () => ({
      scrollToAttachment: () => {
        if (headerRef.current) {
          headerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      },
    }));

    // 检测是否需要显示展开按钮
    useEffect(() => {
      if (listRef.current && attachments.length > 0) {
        // 检测内容高度是否超过容器高度
        const containerHeight = listRef.current.scrollHeight;
        // 设置最大高度为 192px (12rem)，对应原来的 max-h-48
        const maxHeight = 130;
        setShowExpandButton(containerHeight > maxHeight);
      }
    }, [attachments]);

    if (!attachments || attachments.length === 0) {
      return null;
    }

    return (
      <div className="pl-4 w-full mb-8">
        {/* 附件头部 */}
        <div ref={headerRef} className="flex items-center gap-2 w-[calc(100%-32px)] text-xs font-normal mb-2">
          <Paperclip size={16} color="#525252" />
          <span className="text-[#525252]">Attachments ({attachments.length}) :</span>
        </div>
        {/* 附件列表 */}
        <div
          ref={listRef}
          className={`flex flex-wrap gap-2 overflow-hidden transition-[max-height] duration-300 ease-in-out ${
            isExpanded ? 'max-h-none' : 'max-h-[130px]'
          }`}
        >
          {attachments.map((attachment, index) => (
            <AttachmentCard
              {...attachment}
              key={attachment.identity || index}
              onDelete={() => onDeleteAttachment(attachment.identity as string)}
              reUpload={() => onReuploadAttachment(attachment.identity as string)}
            />
          ))}
        </div>

        {/* 展开按钮区域：线-按钮-线 */}
        {showExpandButton && (
          <div className="flex items-center gap-3 mt-3 mb-8 pr-4">
            <div className="flex-1 h-px border-t border-dashed border-gray-200"></div>
            <button
              className="flex items-center justify-center gap-2 px-3 py-1 text-xs text-zinc-500 bg-transparent border-none cursor-pointer transition-all duration-200 shrink-0 hover:text-[#09090B] hover:rounded"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <span>{isExpanded ? 'Collapse' : 'Expand'}</span>
              {isExpanded ? (
                <ChevronUp size={16} className="shrink-0" />
              ) : (
                <ChevronDown size={16} className="shrink-0" />
              )}
            </button>
            <div className="flex-1 h-px border-t border-dashed border-gray-200"></div>
          </div>
        )}
      </div>
    );
  }
);

CollapsibleAttachmentArea.displayName = 'CollapsibleAttachmentArea';

export default CollapsibleAttachmentArea;
